import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";
import { useTranslation } from "@/features/utils/i18n";

const buttonVariants = cva(
  "cursor-pointer! inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,opacity] duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground font-semibold shadow-xs hover:bg-primary/90 active:bg-primary/85",
        destructive:
          "border border-danger/30 bg-danger-soft text-danger-foreground hover:bg-danger/15 dark:hover:bg-danger/25",
        outline:
          "border border-input bg-card shadow-xs hover:bg-muted active:bg-secondary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/70",
        ghost:
          "hover:bg-slate-500/10 dark:hover:text-foreground",
        link: "text-primary-strong font-normal underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 sm:h-9 rounded-full px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 rounded-full px-2 has-[>svg]:px-1.5",
        sm: "h-9 sm:h-8 rounded-full gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-12 sm:h-10 rounded-full px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-full",
        xsicon: "size-6 rounded-full",
        inline: "h-auto p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  confirm?: string;
  tooltip?: string;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  confirm,
  tooltip,
  onClick,
  children,
  ...props
}: ButtonProps) {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const Comp = asChild ? Slot : "button";

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (confirm && !showConfirm) {
      e.preventDefault();
      setShowConfirm(true);
    } else {
      onClick?.(e);
    }
  };

  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
  };

  // Function to render the actual button
  const renderButton = () => {
    if (confirm) {
      return (
        <>
          <Comp
            data-slot="button"
            className={cn(
              buttonVariants({
                variant,
                size: variant === "link" ? "inline" : size,
                className,
              }),
            )}
            onClick={handleClick}
            {...props}
          >
            {children}
          </Comp>
          <ConfirmDialog
            open={showConfirm}
            onOpenChange={setShowConfirm}
            description={confirm}
            onConfirm={handleConfirm}
            confirmDisabled={props.disabled}
          />
        </>
      );
    }

    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({
            variant,
            size: variant === "link" ? "inline" : size,
            className,
          }),
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </Comp>
    );
  };

  // If tooltip is provided, wrap the button with Tooltip
  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{renderButton()}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Return button without tooltip
  return renderButton();
}

/**
 * The confirmation `Button` shows for its `confirm` prop, usable on its own by
 * a control that is not a button (a switch turning something off).
 */
function ConfirmDialog({
  open,
  onOpenChange,
  description,
  onConfirm,
  confirmDisabled,
  title,
  confirmLabel,
  cancelLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description: React.ReactNode;
  onConfirm: (e: React.MouseEvent<HTMLButtonElement>) => void;
  confirmDisabled?: boolean;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title ?? t("common.confirmDialog.title")}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cancelLabel ?? t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={"destructive"}
            onClick={(e) => {
              onOpenChange(false);
              onConfirm(e);
            }}
            disabled={confirmDisabled}
          >
            {confirmLabel ?? t("common.confirmDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { Button, ConfirmDialog, buttonVariants };
